/**
 * Patterns for errors that are expected during normal operation and should
 * NOT be reported to Sentry.  Each entry documents *why* the error is
 * expected so the list stays intentional and auditable.
 *
 * String patterns are matched with `includes` (substring). Use a `RegExp`
 * when the message must be anchored to avoid swallowing app-level errors
 * that happen to share a phrase (e.g. "Failed to fetch repository settings"
 * must not be silenced by the canonical "TypeError: Failed to fetch").
 */
const EXPECTED_ERROR_PATTERNS: ReadonlyArray<{ pattern: string | RegExp; reason: string }> = [
  {
    // Canonical shapes seen in Sentry: "fetch failed", "TypeError: fetch failed",
    // "Network error: fetch failed" (Apollo wrapping undici).
    pattern: /^(?:TypeError: )?(?:Network error: )?fetch failed\.?$/,
    reason:
      'Transient network failure (e.g. DNS timeout, connection reset). ' +
      'Retrying or refreshing resolves it; not actionable in Sentry.'
  },
  {
    // Browser-native: "Failed to fetch" or "TypeError: Failed to fetch".
    // Anchored so app errors like "Failed to fetch repository settings" still report.
    pattern: /^(?:TypeError: )?Failed to fetch\.?$/,
    reason: 'Browser-native variant of "fetch failed". Same transient network error.'
  },
  {
    pattern: 'NavigationDuplicated',
    reason: 'Vue Router 3.x throws when navigating to the current route. Not a bug.'
  },
  {
    pattern: 'Avoided redundant navigation',
    reason: 'Message body of NavigationDuplicated errors.'
  },
  {
    pattern: 'Navigation aborted',
    reason: 'Navigation guards redirecting users — expected control flow, not an error.'
  },
  {
    pattern: 'Redirected when going from',
    reason: 'Navigation guards redirecting users — expected control flow, not an error.'
  },
  {
    pattern: 'The user aborted a request',
    reason: 'User navigated away before fetch completed (AbortError). Not actionable.'
  },
  {
    pattern: 'Signature has expired',
    reason: 'JWT/session expired. UI redirects to login. Not actionable in Sentry.'
  },
  {
    pattern: 'Invariant Violation: 16',
    reason:
      'Apollo clearStore() rejects in-flight queries during logout. ' +
      'Expected and harmless — queries are irrelevant after store reset.'
  },
  {
    pattern: 'GraphQL error: You do not have permission to perform this action',
    reason:
      'Server-side authz denial surfaced as an Apollo error. Normal app behaviour ' +
      'when a user navigates to a resource they cannot access. The UI handles it; ' +
      'reporting it to Sentry just creates noise (~79K events / 7d in production).'
  }
]

/**
 * Returns `true` when `message` matches a known expected-error pattern.
 * Use this in Sentry `beforeSend` and SSR error middleware to keep
 * filtering consistent.  Store catch blocks should always show toasts
 * so users get feedback on failures.
 */
export function isExpectedSentryError(message: string): boolean {
  return EXPECTED_ERROR_PATTERNS.some(({ pattern }) =>
    typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message)
  )
}
